<template>
  <div class="space-y-36">
    <fieldset class="space-y-12">
      <RevInputText
        id="first_name"
        v-model="firstName"
        :disabled
        :error="errors.firstName"
        :label="i18n(translations.firstName)"
      />
      <template v-if="isKanaNameEnabled">
        <RevInputText
          id="kana_first_name"
          v-model="kanaFirstName"
          :disabled
          :error="errors.kanaFirstName"
          :label="i18n(translations.kanaFirstName)"
        />
        <RevInputText
          id="latin_first_name"
          v-model="latinFirstName"
          :disabled
          :error="errors.latinFirstName"
          :label="i18n(translations.latinFirstName)"
        />
      </template>
      <RevInputText
        id="last_name"
        v-model="lastName"
        :disabled
        :error="errors.lastName"
        :label="i18n(translations.lastName)"
      />
      <template v-if="isKanaNameEnabled">
        <RevInputText
          id="kana_last_name"
          v-model="kanaLastName"
          :disabled
          :error="errors.kanaLastName"
          :label="i18n(translations.kanaLastName)"
        />
        <RevInputText
          id="latin_last_name"
          v-model="latinLastName"
          :disabled
          :error="errors.latinLastName"
          :label="i18n(translations.latinLastName)"
        />
      </template>
      <RevInputDate
        id="date_of_birth"
        v-model="dateOfBirth"
        :disabled
        :error="errors.dateOfBirth"
        :label="i18n(translations.dateOfBirth)"
      />
      <RevInputSelect
        id="country_of_citizenship"
        v-model="countryOfCitizenship"
        :disabled
        :error="errors.countryOfCitizenship"
        :label="i18n(translations.countryOfCitizenship)"
        :options="countries"
      />
      <RevInputSelect
        id="country_of_birth"
        v-model="countryOfBirth"
        :disabled
        :error="errors.countryOfBirth"
        :label="i18n(translations.countryOfBirth)"
        :options="countries"
      />
      <RevInputPhone
        id="phone_number"
        v-model="phoneNumber"
        class="sm:mt-8 sm:w-full md:mr-8 md:grow"
        :clear-button-aria-label="i18n(translations.clearPhoneNumber)"
        :default-country="currentMarket.countryCode"
        description=""
        :disabled
        :error="errors.phoneNumber"
        :label="i18n(translations.phoneNumber)"
        :translate="i18n.country"
      />
      <RevInputSelect
        id="country_of_residency"
        v-model="countryOfResidency"
        :disabled
        :error="errors.countryOfResidency"
        :label="i18n(translations.countryOfResidency)"
        :options="countries"
      />
    </fieldset>

    <fieldset class="space-y-12">
      <legend class="body-1-bold mb-12">
        {{ i18n(translations.address) }}
      </legend>

      <AddressFields
        v-model:city="city"
        v-model:country="country"
        v-model:postal-code="postalCode"
        v-model:street="addressLine1"
        v-model:street2="addressLine2"
        class="space-y-12 space-y-reverse"
        :disabled
        :errors="{
          street: errors.addressLine1,
          city: errors.city,
          postalCode: errors.postalCode,
          country: errors.country,
          street2: errors.addressLine2,
        }"
        :feature-code
        :fields="{ firstName: false, lastName: false }"
        :optional-fields="['street2']"
      />
    </fieldset>

    <fieldset class="space-y-12">
      <legend class="body-1-bold mb-12">
        {{ i18n(translations.identityDocument) }}
      </legend>
      <div class="body-2 mb-12">
        {{ i18n(translations.photoIdDescription) }}
      </div>

      <RevInputSelect
        id="identification_type"
        v-model="identificationType"
        :disabled
        :error="errors.identificationType"
        :label="i18n(translations.identificationType)"
        :options="identificationTypes"
      />

      <div v-if="identificationType" class="space-y-12">
        <template
          v-if="identificationType === IDENTIFICATION_TYPES.NATIONAL_ID"
        >
          <RevInputSelect
            id="country_of_issue"
            v-model="countryOfIssue"
            :disabled
            :error="errors.countryOfIssue"
            :label="i18n(translations.countryOfIssue)"
            :options="countries"
          />

          <div class="space-y-12">
            <div class="body-1-bold">
              {{ i18n(translations.uploadFrontSide) }}
            </div>
            <RevInputFileUpload
              id="national_id_front_upload"
              v-model="photoIdFront"
              :accept="['.png', '.jpg', '.jpeg', '.pdf']"
              :description="errors.photoIdFront"
              :disabled
              :errors="errors.photoIdFront as any"
              :label-empty="i18n(translations.uploadFrontSide)"
            />
          </div>

          <div class="space-y-12">
            <div class="body-1-bold">
              {{ i18n(translations.uploadBackSide) }}
            </div>
            <RevInputFileUpload
              id="national_id_back_upload"
              v-model="photoIdBack"
              :accept="['.png', '.jpg', '.jpeg', '.pdf']"
              :description="errors.photoIdBack"
              :disabled
              :errors="errors.photoIdBack as any"
              :label-empty="i18n(translations.uploadBackSide)"
            />
          </div>
        </template>

        <template
          v-else-if="identificationType === IDENTIFICATION_TYPES.PASSPORT"
        >
          <RevInputSelect
            id="country_of_issue"
            v-model="countryOfIssue"
            :disabled
            :error="errors.countryOfIssue"
            :label="i18n(translations.countryOfIssue)"
            :options="countries"
          />

          <div class="space-y-12">
            <div class="body-1-bold">
              {{ i18n(translations.uploadPassportPhoto) }}
            </div>
            <RevInputFileUpload
              id="passport_photo_upload"
              v-model="photoIdFront"
              :accept="['.png', '.jpg', '.jpeg', '.pdf']"
              :description="errors.photoIdFront"
              :disabled
              :errors="errors.photoIdFront as any"
              :label-empty="i18n(translations.uploadPassportPhoto)"
            />
          </div>
        </template>
      </div>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Country } from '@backmarket/http-api'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevInputDate } from '@ds/components/InputDate'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevInputPhone } from '@ds/components/InputPhone'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { IDENTIFICATION_TYPES } from './StakeholderForm.constants'
import translations from './StakeholderForm.translations'

const firstName = defineModel<string>('firstName', { required: true })
const kanaFirstName = defineModel<string>('kanaFirstName')
const latinFirstName = defineModel<string>('latinFirstName')
const lastName = defineModel<string>('lastName', { required: true })
const kanaLastName = defineModel<string>('kanaLastName')
const latinLastName = defineModel<string>('latinLastName')
const dateOfBirth = defineModel<string>('dateOfBirth', { required: true })
const countryOfBirth = defineModel<string>('countryOfBirth', { required: true })
const countryOfCitizenship = defineModel<string>('countryOfCitizenship', {
  required: true,
})
const phoneNumber = defineModel<string>('phoneNumber', { required: true })
const countryOfResidency = defineModel<string>('countryOfResidency', {
  required: true,
})
const addressLine1 = defineModel<string>('addressLine1', { required: true })
const addressLine2 = defineModel<string>('addressLine2')
const postalCode = defineModel<string>('postalCode', { required: true })
const city = defineModel<string>('city', { required: true })
const country = defineModel<Country | ''>('country', { required: true })
const identificationType = defineModel<string>('identificationType', {
  required: true,
})
const countryOfIssue = defineModel<string>('countryOfIssue', { required: true })
const photoIdFront = defineModel<File[]>('photoIdFront', { required: true })
const photoIdBack = defineModel<File[]>('photoIdBack', { required: true })

defineProps<{
  featureCode: FeatureCode
  disabled?: boolean
  errors: {
    firstName?: string
    kanaFirstName?: string
    latinFirstName?: string
    lastName?: string
    kanaLastName?: string
    latinLastName?: string
    dateOfBirth?: string
    countryOfBirth?: string
    countryOfCitizenship?: string
    phoneNumber?: string
    countryOfResidency?: string
    addressLine1?: string
    addressLine2?: string
    postalCode?: string
    city?: string
    country?: string
    identificationType?: string
    countryOfIssue?: string
    photoIdFront?: string
    photoIdBack?: string
  }
}>()

const i18n = useI18n()
const { allMarkets, market: currentMarket } = useMarketplace()
const { isFeatureEnabled } = useBusinessFeaturesStore()

const isKanaNameEnabled = isFeatureEnabled('kanaNameEnabled')

const countries = computed(() =>
  Object.keys(allMarkets).map((countryCode) => ({
    label: i18n.country(countryCode),
    value: countryCode,
  })),
)

const identificationTypes = computed(() => [
  { label: i18n(translations.passport), value: IDENTIFICATION_TYPES.PASSPORT },
  {
    label: i18n(translations.nationalIdentityCard),
    value: IDENTIFICATION_TYPES.NATIONAL_ID,
  },
])
</script>
